import {Injectable} from '@angular/core';
import {UrlBuilderService} from '@common/services/url-builder.service';
import {
  BudgetVsActualForPropertyExpenseCodeRequest,
  BudgetVsActualLeaseRequest,
  BudgetVsActualRequestProperty,
  BudgetVsActualRequestUnit,
  BudgetVsActualUnitApportionmentPercentagesRequest,
  BudgetVsActualUnitApportionmentPercentagesResponse,
  SaveBudgetVsActualNoteRequest
} from '../models/budget-vs-actual-request.model';
import {
  BudgetVsActualItemForPropertyExpenseCodeResponse,
  BudgetVsActualResponse,
  BudgetVsActualUnitCapResponse,
  ServiceChargeData
} from '../models/budget-vs-actual-data.model';
import { HttpClient } from '@angular/common/http';
import {map, mapTo, Observable} from 'rxjs';
import {
  BudgetPeriodDetails,
  BudgetVsActualPeriod,
  BudgetVsActualYear,
  PropertyTaskDetails
} from '../models/budget-vs-actual-period.model';
import {EntityKey} from '@feature/pma-entity/pma-entity-types';
import {
  BudgetVsActualDefaultColumnsAndSortOrder
} from "@feature/_app_modules/budget-vs-actual/BudgetVsActualDefaultColumnsAndOrder";

@Injectable()
export class BudgetVsActualHttpService {

  getByPeriod(request: BudgetVsActualRequestProperty | BudgetVsActualRequestUnit | BudgetVsActualLeaseRequest,
              entityTypeName: string): Observable<BudgetVsActualResponse> {
    const url = this.urls.getUrl(`budgetvsactual/byperiod/${entityTypeName}`);
    return this.http.post<BudgetVsActualResponse>(url, request);
  }

  getCappedAmountForUnit(request: BudgetVsActualRequestUnit): Observable<BudgetVsActualUnitCapResponse> {
    let url = this.urls.getUrl(`budgetvsactual/byperiod/Unit/CappedAmount`);
    return this.http.post<BudgetVsActualUnitCapResponse>(url, request);
  }

  getServiceChargeData(request: BudgetVsActualRequestUnit | BudgetVsActualLeaseRequest, entityTypeName: string): Observable<ServiceChargeData> {
    let url = this.urls.getUrl(`budgetvsactual/byperiod/Unit/ServiceCharge/${entityTypeName}`);
    return this.http.post<ServiceChargeData>(url, request);
  }

  getYears(propertyKey: EntityKey): Observable<BudgetVsActualYear[]> {
    let url = this.urls.getUrl(`budgetvsactual/years/${propertyKey.EntityTypeName}`)
    return this.http.post<{
      Label: string;
      PropertyTaskDetails: PropertyTaskDetails;
      SubstituteLabel: string;
    }[]>(url, propertyKey)
      .pipe(map(periods => periods.map(p =>
        <BudgetVsActualYear>{
          Label: p.Label,
          Value: {
            PropertyTask: p.PropertyTaskDetails,
            DisplayedLabel: p.Label ?? p.SubstituteLabel
          },
          SubstituteLabel: p.SubstituteLabel
        })));
  }

  getPeriods(propertyTaskUri: number): Observable<BudgetVsActualPeriod[]> {
    let url = this.urls.getUrl(`budgetvsactual/${propertyTaskUri}/periods`)
    return this.http.get<{
      Label: string;
      BudgetPeriodDetails: BudgetPeriodDetails;
    }[]>(url)
      .pipe(map(periods => periods.map(p =>
        <BudgetVsActualPeriod>{
          Label: p.Label,
          Value: {
            BudgetPeriod: p.BudgetPeriodDetails
          }
        })));
  }

  getItemLevelData(request: BudgetVsActualForPropertyExpenseCodeRequest): Observable<BudgetVsActualItemForPropertyExpenseCodeResponse> {
    let url = this.urls.getUrl("budgetvsactual/itemforpropertyexpensecode");
    return this.http.post<BudgetVsActualItemForPropertyExpenseCodeResponse>(url, request);
  }

  setSelectedColumns(dashboardWidgetId: number, columns: string[]) {
    let url = this.urls.getUrl("budgetvsactual/setselectedcolumns");
    return this.http.post<object>(url, {Columns: columns, DashboardWidgetId: dashboardWidgetId});
  }

  getSelectedColumns(dashboardWidgetId: number): Observable<string[]> {
    let url = this.urls.getUrl("budgetvsactual/getselectedcolumns");
    return this.http.post<string[]>(url, dashboardWidgetId);
  }

  saveNote(request: SaveBudgetVsActualNoteRequest) {
    let url = this.urls.getUrl("budgetvsactual/savenote");

    return this.http.post(url, request).pipe(mapTo(true));
  }

  getdefaultcolumns(widgetId: number): Observable<BudgetVsActualDefaultColumnsAndSortOrder[]> {
    let url = this.urls.getUrl(`budgetvsactual/getdefaultcolumns/${widgetId}`);
    return this.http.post<BudgetVsActualDefaultColumnsAndSortOrder[]>(url, widgetId);
  }

  getUnitApportionmentPercentages(request: BudgetVsActualUnitApportionmentPercentagesRequest) {
    let url = this.urls.getUrl(`budgetvsactual/getapportionmentpercentages/`);
    return this.http.post<BudgetVsActualUnitApportionmentPercentagesResponse[]>(url, request);
  }

  constructor(
    private urls: UrlBuilderService,
    private http: HttpClient
  ) {
  }
}
